/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : PageLayout
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical page shell — consistent page padding plus the
 *                  shared <PageHeader>. Every screen starts here instead of an
 *                  ad-hoc `<div className="p-6">` + a bespoke <h1>. Keeps page
 *                  gutters, header, and max width identical across the portal.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { clsx } from 'clsx';
import { PageHeader } from '../layout/PageHeader';

export interface PageLayoutProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Cap content width for readability on ultrawide screens. Default 'full'. */
  maxWidth?: 'full' | 'wide';
  className?: string;

  // ── Spec 226 — Universal Page Affordances (forwarded to PageHeader) ──
  /** Registry key for this page's contextual help; renders the `?` panel. */
  helpId?: string;
  /** Soft re-fetch action; renders the standard Refresh control. */
  onRefresh?: () => void | Promise<void>;
  /** True while a refresh is in flight. */
  isRefreshing?: boolean;
  /** Last client fetch time (freshness indicator). */
  lastFetchedAt?: Date | number | string | null;
  /** DB collection time, best-effort (freshness indicator). */
  collectedAt?: Date | number | string | null;
  /** Last refresh error; renders the non-blocking error badge. */
  refreshError?: unknown;
}

/** Canonical page wrapper. Provides gutters + the shared header. */
export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  description,
  icon,
  actions,
  children,
  maxWidth = 'full',
  className,
  helpId,
  onRefresh,
  isRefreshing,
  lastFetchedAt,
  collectedAt,
  refreshError,
}) => (
  <div className="p-4 sm:p-6">
    <div className={clsx('mx-auto', maxWidth === 'wide' ? 'max-w-screen-2xl' : 'w-full', className)}>
      {title != null && (
        <PageHeader
          title={title}
          description={description}
          icon={icon}
          actions={actions}
          helpId={helpId}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
          lastFetchedAt={lastFetchedAt}
          collectedAt={collectedAt}
          refreshError={refreshError}
        />
      )}
      {children}
    </div>
  </div>
);

export default PageLayout;
